import {
  type ApproverDecision,
  BATCH_TREATMENT,
  BATCHABLE_DISPOSITIONS,
  type BatchableDisposition,
  type BatchDeclaration,
  DecisionBatchRequestSchema,
  type Severity,
  SEVERITIES,
} from '@zevik/schemas';
import type { FindingEntry } from '../api/client';
import type { DecisionRequest } from './decision';

/**
 * A group decision from the workspace: which findings are selected, and one
 * verdict applied to them (docs/DECISIONS.md ZD-127 §3).
 *
 * ## Why this exists, measured
 *
 * The first live review recorded 38 decisions, 35 of them in one call at one
 * millisecond through a route the console does not use: deciding thirty-six
 * findings one press at a time was not worth doing even once, in a demo, by
 * the person who built the screen. The case that prompted it is *select the
 * twelve mediums and accept them*, and the list is already ordered by band —
 * so the selection is filtered by facts each row already renders, and nothing
 * here reads anything the list does not.
 *
 * ## What is offered is what the schema records, read rather than restated
 *
 * `BATCHABLE_DISPOSITIONS` is the schema's own list — accept, with the one
 * treatment a batch may carry, and reject. A deferral, an accepted risk, a
 * rewrite and a dissent are each a judgement about one finding, and the
 * record refuses them in a batch (`ApproverDecisionSchema`); this bar does
 * not offer them, and a caller that sends one is told by the API rather than
 * by a sentence composed here.
 *
 * ## The pre-flight is the API's schema, so the sentence is the API's own
 *
 * `batchProblems` runs `DecisionBatchRequestSchema` — the same parse
 * `recordDecisions` runs before any member is stamped — over the request this
 * bar would send, and relays its issues. A reject with no reason, a
 * selection of one, and a finding named twice are refused here in the words
 * the API would use, before anything is sent (ZD-118 §3's convention).
 * Nothing here decides that a batch is *acceptable*; the API does.
 *
 * ## The reason is the group's, and nothing is copied onto a member
 *
 * Every member goes with `reason: null`. The one sentence a reviewer types
 * travels once, on `batch`, and the record holds it once (ZD-127 §2).
 */

/** What the list can be narrowed by, each a fact the row already renders. */
export interface ListFilter {
  band: Severity | 'any';
  standing: 'any' | 'undecided' | 'decided';
  conditional: 'any' | 'conditional' | 'unconditional';
}

export const emptyFilter = (): ListFilter => ({
  band: 'any',
  standing: 'any',
  conditional: 'any',
});

/** The bands the filter offers, strongest first — the schema's order, read. */
export const FILTER_BANDS: readonly Severity[] = SEVERITIES;

/**
 * The findings a filter keeps, in the order given. `reportedBand` is the
 * band the row prints, and a finding it prints no band for matches no band
 * filter — it is unranked, not weakest (`review/order.ts`).
 */
export function filterFindings(
  findings: readonly FindingEntry[],
  standing: ReadonlyMap<string, ApproverDecision>,
  filter: ListFilter,
  bandOf: (entry: FindingEntry) => Severity | null,
): FindingEntry[] {
  return findings.filter((entry) => {
    if (filter.band !== 'any' && bandOf(entry) !== filter.band) return false;
    const decided = standing.has(entry.finding_key);
    if (filter.standing === 'undecided' && decided) return false;
    if (filter.standing === 'decided' && !decided) return false;
    const conditional = entry.finding.conditional.is_conditional;
    if (filter.conditional === 'conditional' && !conditional) return false;
    if (filter.conditional === 'unconditional' && conditional) return false;
    return true;
  });
}

/** What the batch bar holds before it is sent. */
export interface BatchDraft {
  disposition: BatchableDisposition;
  reason: string;
}

export const emptyBatchDraft = (): BatchDraft => ({ disposition: 'accept', reason: '' });

/** What each batchable verdict is called on the bar. */
export const BATCH_LABELS: Readonly<Record<BatchableDisposition, string>> = {
  accept: 'These are true of their system — remediate',
  reject: 'These are not true of their system',
};

/** The request a selection and a draft would send, not yet judged. */
export function batchRequest(
  selection: readonly FindingEntry[],
  draft: BatchDraft,
): { batch: BatchDeclaration; decisions: DecisionRequest[] } {
  const reason = draft.reason.trim();
  return {
    batch: { reason: reason.length === 0 ? null : reason },
    decisions: selection.map((entry) => ({
      finding_key: entry.finding_key,
      finding_structural_key: entry.finding_structural_key,
      finding_id: entry.finding.finding_id,
      pack_id: entry.finding.pack_id,
      pack_version: entry.finding.pack_version,
      disposition: draft.disposition,
      treatment: draft.disposition === 'accept' ? BATCH_TREATMENT : null,
      reason: null,
      edits: [],
      severity_dissent: null,
    })),
  };
}

/** One problem with a batch, and the member it is about where it is about one. */
export interface BatchProblem {
  message: string;
  /** The selected finding the issue names, resolved from the issue's path. */
  finding: { finding_id: string; title: string } | null;
}

/**
 * Resolves the API's issues against the members that were sent, in order.
 * An issue whose path is `decisions[i]…` is about the i-th member sent; the
 * screen names it by title, because a position is not something a reviewer
 * recognises (ZD-043 §8). Any other path is about the batch as a whole.
 */
export function problemsFrom(
  issues: readonly { path: readonly (string | number | symbol)[]; message: string }[],
  members: readonly FindingEntry[],
): BatchProblem[] {
  return issues.map((issue) => {
    const [head, index] = issue.path;
    const member = head === 'decisions' && typeof index === 'number' ? members[index] : undefined;
    return {
      message: issue.message,
      finding:
        member === undefined
          ? null
          : { finding_id: member.finding.finding_id, title: member.finding.title },
    };
  });
}

/**
 * Why a batch is not yet sendable, in the API's words. Empty means send it.
 *
 * The schema is the one `recordDecisions` judges the set with; running it
 * here is what makes every sentence on the bar the API's own rather than a
 * second expression of the same rules that nothing compares to the first.
 */
export function batchProblems(
  selection: readonly FindingEntry[],
  draft: BatchDraft,
): BatchProblem[] {
  const parsed = DecisionBatchRequestSchema.safeParse(batchRequest(selection, draft));
  if (parsed.success) return [];
  return problemsFrom(parsed.error.issues, selection);
}

/**
 * A draft as the request body, or a throw naming what is wrong.
 *
 * **Refuses rather than repairs**, as `buildDecisionRequest` does: a builder
 * that quietly dropped a reason, or sent one member's decision as a
 * different verdict, would record something the reviewer did not say.
 */
export function buildBatchRequest(
  selection: readonly FindingEntry[],
  draft: BatchDraft,
): { batch: BatchDeclaration; decisions: DecisionRequest[] } {
  const problems = batchProblems(selection, draft);
  if (problems.length > 0) {
    throw new Error(
      `this group decision is not sendable: ${problems.map((p) => p.message).join(' ')}`,
    );
  }
  return batchRequest(selection, draft);
}

export { BATCHABLE_DISPOSITIONS };
